<template lang="pug">
.map-loader
  .loader-inner(v-if="model.loading")
    .spinner
    p.loader-label Fetching map list…

  .loader-inner(v-else-if="model.error")
    font-awesome-icon.error-icon(icon="fa-solid fa-circle-exclamation")
    p.error-title Failed to load map
    p.error-msg {{ model.error }}

  .loader-inner(v-else)
    .loader-header
      p.loader-source Quaddicted
      h2.loader-title {{ model.map?.title }}
    .progress-wrap
      .progress-track
        .progress-fill(:style="{ width: progressPercent + '%' }")
    .loader-meta
      span.loader-message {{ mapsStore.getMapLoadProgress.message }}
      span.loader-kb(v-if="loadedKb") {{ loadedKb }}
</template>

<script lang="ts" setup>
import { reactive, onMounted, computed, watch } from 'vue'
import { useMapsStore } from '../../../stores/maps'
import { useRoomStore } from '../../../stores/room'
import type { QuaddictedMap } from '../../../types/QuaddictedMap'
import type { SourceId } from '../../../../../shared/types/Source'

const mapsStore = useMapsStore()
const roomStore = useRoomStore()

// Report download progress to the room server so other players can see it
watch(() => mapsStore.getMapLoadProgress, (progress) => {
  if (roomStore.serverConnection) {
    roomStore.sendAssetProgress(progress.loaded, progress.total)
  }
}, { deep: true })

const emit = defineEmits<{ (e: 'done'): void }>()

const props = defineProps<{ sourceId: SourceId }>()

const model = reactive<{
  map: QuaddictedMap | null
  error: string
  loading: boolean
}>({
  map: null,
  error: '',
  loading: true,
})

const addCommas = (x: number) => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const loadedKb = computed(() => {
  if (!mapsStore.getMapLoadProgress.total) return ''
  const total = addCommas(Math.floor(mapsStore.getMapLoadProgress.total / 1024))
  const loaded = addCommas(Math.floor(mapsStore.getMapLoadProgress.loaded / 1024))
  return `${loaded} / ${total} KB`
})

const progressPercent = computed(() => {
  if (!mapsStore.getMapLoadProgress.total) return 0
  return Math.min(100, Math.ceil((mapsStore.getMapLoadProgress.loaded / mapsStore.getMapLoadProgress.total) * 100))
})

onMounted(() => {
  const [_quaddicted, mapId] = props.sourceId.split(':')
  mapsStore.loadMapListing()
    .catch(e => {
      model.loading = false
      model.error = 'Error loading map list: ' + e.message
      return Promise.reject(e)
    })
    .then(() => {
      model.map = mapsStore.getMapFromId(mapId)
      model.loading = false
      if (model.map) {
        return mapsStore.loadMap(props.sourceId)
          .then(() => {
            roomStore.sendAssetProgress(0, 0) // clear progress
            emit('done')
          })
          .catch(e => { model.error = 'Error loading map: ' + e.message })
      } else {
        model.error = 'Map not recognised'
      }
    })
})
</script>

<style lang="scss" scoped>
@import '../../../scss/tokens';

.map-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  padding: 48px 24px;
}

.loader-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 480px;
  text-align: center;
}

// Loading state
.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid $palette-border;
  border-top-color: $palette-red;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.loader-label {
  font-size: $font-sm;
  color: $palette-muted;
}

// Error state
.error-icon {
  font-size: 32px;
  color: $palette-red;
}

.error-title {
  font-size: $font-base;
  font-weight: $fw-bold;
  color: $palette-bright;
}

.error-msg {
  font-size: $font-sm;
  color: $palette-muted;
  line-height: 1.6;
}

// Progress state
.loader-header { margin-bottom: 8px; }

.loader-source {
  font-size: $font-2xs;
  font-weight: $fw-bold;
  text-transform: uppercase;
  letter-spacing: $tracking-labels;
  color: $palette-red;
  margin-bottom: 6px;
}

.loader-title {
  font-size: $font-md;
  font-weight: $fw-black;
  color: $palette-bright;
  letter-spacing: -0.01em;
}

.progress-wrap {
  width: 100%;
  margin: 4px 0;
}

.progress-track {
  width: 100%;
  height: 3px;
  background: $palette-border;
}

.progress-fill {
  height: 100%;
  background: $palette-red;
  transition: width 0.3s ease;
  min-width: 2px;
}

.loader-meta {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.loader-message {
  font-size: $font-xs;
  color: $palette-muted;
  text-align: left;
}

.loader-kb {
  font-size: $font-xs;
  font-weight: $fw-semibold;
  color: $palette-text;
  font-family: 'JetBrains Mono', monospace;
  white-space: nowrap;
  flex-shrink: 0;
}
</style>
